
import {defineComponent} from 'vue';
import ToTop from "@/components/shared/TopButtonComponent.vue";
import paycoUtils from "@/assets/js/utils";
import InformationComponent from "@/components/shared/InformationComponent.vue";
import PopupComponent from "@/components/shared/PopupComponent.vue";
import router from "@/router";
import {NamedRouting} from "@/domain/utils/NamedRouting";
import {Item} from "@/domain/ui/Item";
import api from "@/api/backend-api";
import HeaderComponent from "@/components/shared/HeaderComponent.vue";
import {mapGetters} from "vuex";
import store from "@/store";
import ComplexPayment from "@/domain/order/ComplexPayment";

export default defineComponent({
  name: 'OrderDetail',
  props: ['orderId'],
  components: {
    HeaderComponent,
    InformationComponent,
    ToTop,
    PopupComponent
  },
  data() {
    return {
      order: {
        orderedYmdt: '',
        orderType: '', // PURCHASE 또는 GIFT
        orderAmount: 0,
        paymentAmount: 0,
        paymentMethod: '',
        complexAspSaveAmount: 0,
        complexAspPayAmount: 0,
        isCancelable: true,
        cancelCount: 0,
      },
      product: {
        productName: '',
        productImageUrl: '',
        brandName: ''
      },
      tickets: [
        {
          receiverName: '',
          representativeName: '',
          ticketId: '',
          ticketStatus: '',
          isCancelable: false,
        },
      ],

      orderInformation: [] as Array<Item>,
      paymentInformation: [] as Array<Item>,
      showDetail: new Map<string, boolean>(),
      showCancelPopup: false,
      showCancelCompletionPopup: false,
      selectedTicketId: '',
    }
  },
  mounted() {
    this.getOrder();
  },
  computed: {
    ticketGroupedByName(): Map<string, Array<any>> { //todo any to class
      return paycoUtils.groupBy(this.tickets, "representativeName");
    },
    isGiftOrder(): boolean {
      return this.order.orderType === 'GIFT';
    },
    orderTypeStatement(): string {
      return this.isGiftOrder ? '선물' : '주문';
    },
    cancelButtonStatement(): string {
      return this.isGiftOrder ? '선물' : '주문';
    },
    showDimemed(): boolean {
      return this.showCancelPopup || this.showCancelCompletionPopup;
    },
    ...mapGetters('AppYnStore', [
      'isApp'
    ]),
  },
  methods: {
    getOrder() {
      api.getOrderDetail(this.orderId)
      .then(response => {
        if (response.data.code === 4444) {
          if (store.getters['AppYnStore/isApp']) {
            router.push(new NamedRouting('NonMemberViewForApp', {channelId: store.state.ChannelStore.channelId}));
          } else {
            store.commit('NextViewStore/setNextView', new NamedRouting('OrderDetailView', {orderId: this.orderId}));
            router.push(new NamedRouting('NonMemberView', store.state.ChannelStore.channelId));
          }
          return;
        }

        const orderData = response.data.result;
        this.order.orderedYmdt = orderData.orderYmdt;
        this.order.orderType = orderData.orderType;
        this.order.orderAmount = orderData.orderAmount;
        this.order.paymentAmount = orderData.paymentAmount;
        this.order.paymentMethod = orderData.paymentMethod;

        orderData.complexPayments.forEach((complexPayment: ComplexPayment) => {
          switch (complexPayment.paymentMethod) {
            case 'ASP_MEMBERSHIP_SAVE':
              this.order.complexAspSaveAmount = complexPayment.paymentAmount;
              break;
            case 'ASP_MEMBERSHIP_PAY':
              this.order.complexAspPayAmount = complexPayment.paymentAmount;
              break;
          }
        });

        this.product = orderData.product;

        this.tickets = orderData.ticketList;
        this.tickets.forEach((ticket: any) => {
          ticket.isCancelable = paycoUtils.convertYnToBoolean(ticket.cancelableYn);
          if (!ticket.isCancelable) { // 티켓 하나라도 취소 불가면 주문 취소 불가
            this.order.isCancelable = false;
          }
          if (this.isCanceled(ticket.ticketStatus)) {
            this.order.cancelCount++;
          }
          ticket.representativeName = paycoUtils.isEmptyString(ticket.receiverName) ? ticket.receiverTelNo : ticket.receiverName;
        });
      })
      .then(() => {
        this.orderInformation = [];
        this.paymentInformation = [];

        this.orderInformation.push(new Item('주문일시', this.order.orderedYmdt));
        this.orderInformation.push(new Item('상품명', this.product.productName));
        this.orderInformation.push(new Item('사용처', this.product.brandName));
        this.orderInformation.push(new Item('주문 금액', paycoUtils.numWithCommas(this.order.orderAmount) + '원'));

        if (this.order.isCancelable) {
          this.orderInformation.push(new Item('주문 상태', '결제완료', false, true, false, true, this.orderTypeStatement + '취소'));
        } else if (this.order.cancelCount > 0) {
          this.orderInformation.push(new Item('주문 상태', (this.order.cancelCount != this.tickets.length ? '부분' : this.orderTypeStatement) + '취소 내역보기', false, false, true));
        } else {
          this.orderInformation.push(new Item('주문 상태', '결제완료', false, true));
        }


        this.paymentInformation.push(new Item('주문 금액', paycoUtils.numWithCommas(this.order.orderAmount + '원')));
        if (this.order.complexAspSaveAmount > 0) {
          this.paymentInformation.push(new Item('포인트 사용', '- ' + paycoUtils.numWithCommas(this.order.complexAspSaveAmount) + '원'));
        }
        if (this.order.complexAspPayAmount > 0) {
          this.paymentInformation.push(new Item('충전금 사용', '- ' + paycoUtils.numWithCommas(this.order.complexAspPayAmount) + '원'));
        }
        this.paymentInformation.push(new Item('총 결제 금액', paycoUtils.numWithCommas(this.order.paymentAmount) + '원'));
        if (this.order.paymentMethod != 'NONE') {
          this.paymentInformation.push(new Item('결제 수단', this.order.paymentMethod));
        }

        this.tickets.forEach(ticket => {
          this.showDetail.set(ticket.receiverName, false);
        });

        if (this.showDetail.size === 1) {
          this.showDetail.set(this.tickets[0].receiverName, true);
        }
      })
      .catch(error => {
        console.log("Error: " + error);
        this.$router.back();
      });
    },
    statusStatement(ticketStatus: string): string {
      switch (ticketStatus) {
        case 'USED':
        case 'REFUNDED':
          return '사용완료';
        case 'ISSUED':
          return '미사용';
        case 'REJECT':
        case 'CANCEL':
          return '취소';
        default:
          return '';
      }
    },
    isCanceled(ticketStatus: string): boolean {
      return ticketStatus === 'CANCEL';
    },
    isRejected(ticketStatus: string): boolean {
      return ticketStatus === 'REJECT';
    },
    canDelete(ticketStatus: string): boolean {
      switch (ticketStatus) {
        case 'USED':
        case 'REFUNDED':
        case 'REJECT':
        case 'CANCEL':
          return true;
        case 'ISSUED':
        default:
          return false;
      }
    },
    toggleReceiverDetail(receiverName: string): void {
      this.showDetail.set(receiverName, !this.showDetail.get(receiverName));
    },
    showReceiverDetail(receiverName: string): boolean {
      return this.showDetail.get(receiverName)!;
    },
    askIfCancel(ticketId: string): void {
      this.selectedTicketId = ticketId;
      this.toggleCancelPopup();
    },
    toggleCancelPopup(): void {
      this.showCancelPopup = !this.showCancelPopup;
    },
    confirmCancel(isConfirmed: boolean): void {
      if (isConfirmed) {
        api.cancelOrder(this.orderId)
        .then(response => {
          if (response.data.code === 0) {
            this.toggleCancelCompletionPopup();
            this.getOrder();
            return;
          }

          if (response.data.code === 4444) {
            if (store.getters['AppYnStore/isApp']) {
              router.push(new NamedRouting('NonMemberViewForApp', {channelId: store.state.ChannelStore.channelId}));
            } else {
              store.commit('NextViewStore/setNextView', new NamedRouting('OrderDetailView', {orderId: this.orderId}));
              router.push(new NamedRouting('NonMemberView', store.state.ChannelStore.channelId));
            }
            return;
          }

          alert('주문 취소가 실패하였습니다. 잠시 후, 다시 시도해주세요.');
        })
        .catch(error => {
          console.log("Error: " + error);
        });
      }
      this.selectedTicketId = '';
      this.toggleCancelPopup();
    },
    toggleCancelCompletionPopup(): void {
      this.showCancelCompletionPopup = !this.showCancelCompletionPopup;
    },

    moveToCancellationHistory(ticketId: string): void {
      router.push(new NamedRouting('PaymentCancellationHistoryView', {ticketId: ticketId}));
    },

    moveToOrderCancellationHistory(): void {
      router.push(new NamedRouting('PaymentCancellationHistoryView', {orderId: this.orderId}));
    }
  }
});

